import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  Save,
  Link as LinkIcon,
  Share2,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  {
    id: 'squarespace',
    name: 'Squarespace',
    steps: [
      'Open the page where you want the booking form',
      'Add a new block → choose the "Code" block',
      'Paste your embed code, then click Apply',
    ],
  },
  {
    id: 'wix',
    name: 'Wix',
    steps: [
      'Click "+ Add" in your Wix editor',
      'Choose "Embed Code" → "Embed HTML"',
      'Paste your embed code and click Update',
    ],
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    steps: [
      'Edit the page where you want the form',
      'Add a new block → search for "Custom HTML"',
      'Paste your embed code and click Update',
    ],
  },
  {
    id: 'showit',
    name: 'Showit',
    steps: [
      'Add a new widget to your page',
      'Choose "Embed Code" or "HTML Widget"',
      'Paste your embed code and publish',
    ],
  },
  {
    id: 'html',
    name: 'Custom HTML',
    steps: [
      "Open your website's HTML file",
      'Paste the embed code where you want the form',
      'Save and upload your file',
    ],
  },
];

function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };
  return (
    <div className="relative">
      <pre className="bg-[#0f0d0a] border border-border rounded-lg p-4 pr-28 text-xs sm:text-sm text-foreground overflow-x-auto font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={onCopy}
        className="absolute top-3 right-3 bg-primary text-background hover:bg-primary/90 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2"
        data-testid={testid}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" /> Copied!
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" /> Copy Code
          </>
        )}
      </button>
    </div>
  );
}

export default function EmbedCode() {
  const navigate = useNavigate();
  const domain = window.location.origin;

  const [loading, setLoading] = useState(true);
  const [savedSlug, setSavedSlug] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);
  const [embedTab, setEmbedTab] = useState('quick');
  const [platform, setPlatform] = useState('squarespace');
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings`, { withCredentials: true });
        const s = res.data?.booking_slug || '';
        setSavedSlug(s);
        setSlug(s);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isValidSlug = (v) => !v || /^[a-z0-9-]+$/.test(v);
  const handleSlugChange = (e) => {
    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(v);
    setSaveError('');
    setSaveOk(false);
  };

  const saveSlug = async () => {
    if (!isValidSlug(slug)) {
      setSaveError('Use lowercase letters, numbers, and hyphens only.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await axios.patch(`${API}/settings`, { booking_slug: slug || null }, { withCredentials: true });
      setSavedSlug(slug);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 3000);
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Failed to save. Try a different name.');
    } finally {
      setSaving(false);
    }
  };

  const bookingUrl = savedSlug ? `${domain}/book/${savedSlug}` : '';
  const previewUrl = savedSlug ? `${domain}/book/${savedSlug}` : '';

  const quickCode = savedSlug
    ? `<script src="${domain}/embed.js"></script>\n<div data-photog-bizkit-booking="${savedSlug}"></div>`
    : '';
  const iframeCode = savedSlug
    ? `<iframe src="${domain}/book/${savedSlug}?embed=true" width="100%" height="700" frameborder="0" style="border:none;"></iframe>`
    : '';

  const copyBookingUrl = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const StepHeader = ({ n, title, done }) => (
    <div className="flex items-center gap-3 mb-4">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
          done ? 'bg-success/20 text-success' : 'bg-primary/15 text-primary'
        }`}
      >
        {done ? <Check className="w-4 h-4" /> : n}
      </div>
      <h2 className="text-lg sm:text-xl font-semibold text-foreground">{title}</h2>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-text-secondary hover:text-foreground mb-4 transition-colors text-sm"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Booking Widget Setup
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-2" data-testid="widget-setup-title">
            Turn your website into a booking magnet
          </h1>
          <p className="text-text-secondary">
            Pick a URL, grab a snippet, drop it into your site. Clients can self-book — the meeting lands straight in your Schedule.
          </p>
        </div>

        {loading ? (
          <div className="text-text-secondary">Loading…</div>
        ) : (
          <>
            {/* STEP 1 — Slug */}
            <section
              className="bg-surface border border-border rounded-2xl p-5 sm:p-6 mb-5"
              data-testid="step-slug"
            >
              <StepHeader n={1} title="Choose your booking URL" done={!!savedSlug} />

              <label className="block text-sm font-medium text-text-muted mb-2">Your booking URL</label>
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <div className="flex items-stretch bg-surface-raised border border-border rounded-lg overflow-hidden flex-1">
                  <span className="px-3 py-2 text-sm text-text-muted bg-background/50 border-r border-border select-none whitespace-nowrap">
                    {domain.replace(/^https?:\/\//, '')}/book/
                  </span>
                  <input
                    type="text"
                    value={slug}
                    onChange={handleSlugChange}
                    placeholder="your-studio-name"
                    className="flex-1 bg-transparent px-3 py-2 text-sm text-foreground focus:outline-none min-w-0"
                    data-testid="slug-input"
                    autoComplete="off"
                  />
                </div>
                <button
                  onClick={saveSlug}
                  disabled={saving || slug === savedSlug}
                  className="px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors bg-primary text-background hover:bg-primary/90 disabled:bg-surface-raised disabled:text-text-muted disabled:cursor-not-allowed"
                  data-testid="slug-save-button"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>

              {slug && (
                <p className="text-sm text-text-muted" data-testid="slug-live-preview">
                  Your booking page:{' '}
                  <span className="text-foreground font-mono">
                    {domain}/book/{slug}
                  </span>
                </p>
              )}

              {saveError && (
                <div className="flex items-start gap-2 text-sm text-alert mt-3" data-testid="slug-error">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <span>{saveError}</span>
                </div>
              )}

              {saveOk && savedSlug && (
                <div
                  className="mt-4 bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-3"
                  data-testid="slug-saved-banner"
                >
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground font-medium">Your booking page is live!</p>
                  </div>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm px-3 py-1.5 rounded-lg bg-surface-raised hover:bg-surface border border-border text-foreground flex items-center gap-1.5"
                    data-testid="preview-live"
                  >
                    <ExternalLink className="w-4 h-4" /> Preview
                  </a>
                </div>
              )}

              <p className="text-xs text-text-muted mt-3">Lowercase letters, numbers, and hyphens only.</p>
            </section>

            {/* STEP 2 — Embed code */}
            {savedSlug && (
              <section
                className="bg-surface border border-border rounded-2xl p-5 sm:p-6 mb-5"
                data-testid="step-embed"
              >
                <StepHeader n={2} title="Add to your website" />

                <div className="flex gap-1 bg-background/50 border border-border rounded-lg p-1 w-fit mb-4">
                  <button
                    onClick={() => setEmbedTab('quick')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      embedTab === 'quick' ? 'bg-primary text-background' : 'text-text-secondary hover:text-foreground'
                    }`}
                    data-testid="tab-quick"
                  >
                    Quick Embed <span className="text-xs opacity-70 ml-1">recommended</span>
                  </button>
                  <button
                    onClick={() => setEmbedTab('iframe')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      embedTab === 'iframe' ? 'bg-primary text-background' : 'text-text-secondary hover:text-foreground'
                    }`}
                    data-testid="tab-iframe"
                  >
                    Simple iFrame
                  </button>
                </div>

                {embedTab === 'quick' ? (
                  <>
                    <p className="text-sm text-text-secondary mb-3">
                      Auto-resizes to fit your content — no scrollbars, blends with any website.
                    </p>
                    <CodeBlock code={quickCode} testid="copy-quick-embed" />
                  </>
                ) : (
                  <>
                    <p className="text-sm text-text-secondary mb-3">
                      Fixed-height iframe. Works everywhere — the simplest option.
                    </p>
                    <CodeBlock code={iframeCode} testid="copy-iframe-embed" />
                  </>
                )}
              </section>
            )}

            {/* STEP 3 — Platform instructions */}
            {savedSlug && (
              <section
                className="bg-surface border border-border rounded-2xl p-5 sm:p-6 mb-5"
                data-testid="step-platform"
              >
                <StepHeader n={3} title="Where are you adding it?" />

                <div className="flex flex-wrap gap-2 mb-5">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPlatform(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        platform === p.id
                          ? 'bg-primary text-background'
                          : 'bg-surface-raised text-text-secondary hover:text-foreground border border-border'
                      }`}
                      data-testid={`platform-${p.id}`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>

                <ol className="space-y-3">
                  {PLATFORMS.find((p) => p.id === platform).steps.map((s, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-foreground"
                      data-testid={`platform-step-${i}`}
                    >
                      <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-text-secondary leading-relaxed pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* STEP 4 — Share link */}
            {savedSlug && (
              <section
                className="bg-surface border border-border rounded-2xl p-5 sm:p-6 mb-5"
                data-testid="step-share"
              >
                <StepHeader n={4} title="Or share your booking page directly" />

                <div className="flex items-center gap-2 mb-3 bg-background/50 border border-border rounded-lg px-3 py-2">
                  <LinkIcon className="w-4 h-4 text-text-muted flex-shrink-0" />
                  <span
                    className="flex-1 text-sm text-foreground font-mono truncate"
                    data-testid="share-url"
                  >
                    {bookingUrl}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={copyBookingUrl}
                    className="px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                    data-testid="share-copy"
                  >
                    {urlCopied ? (
                      <>
                        <Check className="w-4 h-4" /> Copied
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4" /> Copy link
                      </>
                    )}
                  </button>
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-surface-raised hover:bg-surface border border-border rounded-lg transition-colors flex items-center gap-2 text-sm font-medium text-foreground"
                    data-testid="share-open"
                  >
                    <ExternalLink className="w-4 h-4" /> Open in new tab
                  </a>
                </div>

                <p className="text-xs text-text-muted mt-3">
                  Drop this link in your Instagram bio, email signature, or anywhere online.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
